import {
  AnalyticsIcon,
  CalendarIcon,
  MoodboardIcon,
  Settings2Icon,
} from '@/components/ui/icons/icons-new'
import { Badge } from '@/components/ui/shadcn/badge'
import { useUserContext } from '@/context/userLogin'
import { useIsAbove768 } from '@/hooks/useMediaQuery'
import { useSidebarStore } from '@/stores/sidebarStore'
import { CircleUserRound, Flag, Workflow } from 'lucide-react'
import { Link, useLocation } from 'react-router'

const iconMap = {
  '/moodboards': MoodboardIcon,
  '/automations': Workflow,
  '/posts': CalendarIcon,
  // TODO: come up with icon
  '/campaign': Flag,
  '/analytics': AnalyticsIcon,
  '/admin': AnalyticsIcon,
  // Brand-scoped settings keep the gear; the user-level Account tab gets a person.
  '/settings': Settings2Icon,
  '/account': CircleUserRound,
}

type SidebarItem = { label: string; to: string; badge?: string; disabled?: boolean }

// `showAdmin` gates the admin link so it renders once even though the sidebar
// mounts this component twice (brand-scoped nav up top, Account at the bottom).
export function SidebarNavigationListItem({
  items,
  showAdmin = true,
}: {
  items: SidebarItem[]
  showAdmin?: boolean
}) {
  const location = useLocation()
  const { closeSidebar } = useSidebarStore()
  const isAbove768 = useIsAbove768()
  const { user } = useUserContext()

  return (
    <ul className="flex flex-col gap-0.5 py-1">
      {items.map((item) => {
        const Icon = iconMap[item.to as keyof typeof iconMap]
        return (
          <li
            key={item.to}
            className={`rounded-sm ${item.disabled ? 'cursor-default opacity-60' : `cursor-pointer ${location.pathname.startsWith(item.to) ? 'bg-hover' : 'hover:bg-hover'}`}`}>
            {item.disabled ? (
              <span className="flex items-center gap-2 px-2 py-1">
                {Icon && <Icon width={20} height={20} color="var(--foreground)" />}
                {item.label}
                {item.badge && (
                  <Badge variant="secondary" className="ml-auto px-1.5 py-0 text-[10px]">
                    {item.badge}
                  </Badge>
                )}
              </span>
            ) : (
              <Link
                to={item.to}
                onClick={!isAbove768 ? closeSidebar : undefined}
                className="flex items-center gap-2 px-2 py-1">
                {Icon && <Icon width={20} height={20} color="var(--foreground)" />}
                {item.label}
                {item.badge && (
                  <Badge variant="secondary" className="ml-auto px-1.5 py-0 text-[10px]">
                    {item.badge}
                  </Badge>
                )}
              </Link>
            )}
          </li>
        )
      })}
      {user?.isAdmin && showAdmin && (
        <li
          className={`cursor-pointer rounded-sm text-base ${location.pathname.startsWith('/admin') ? 'bg-hover' : 'hover:bg-hover'}`}>
          <Link
            to="/admin"
            onClick={!isAbove768 ? closeSidebar : undefined}
            className="flex items-center gap-2 px-2 py-1">
            <AnalyticsIcon width={20} height={20} color="var(--foreground)" />
            Admin Dashboard
          </Link>
        </li>
      )}
    </ul>
  )
}
